// ---------------------------------------------------------------------------
// Single source of truth for the application's gated routes.
//
// Routes, side navigation and the Access Denied entitlement experience are all
// derived from this registry, so adding a page or a role only requires an entry
// here: the nav item, the route guard and the entitlement copy/CTA adapt on
// their own.
//
// `group` is the human readable area name surfaced to the user ("The page you
// are trying to access is part of {group}..."), and `roles` is the OR-list of
// app roles allowed to open the route. Order matters: it drives the nav order,
// the default landing route and the suggested route on the entitlement page.
// ---------------------------------------------------------------------------

import React from 'react';
import {
  EditRegular,
  PersonAvailableRegular,
  CalendarLtrRegular,
  ClockRegular,
  WrenchRegular,
  PersonAddRegular,
  AlertRegular,
  QuestionCircleRegular,
} from '@fluentui/react-icons';
import { PDM_READ_ROLES, MARKETING_ROLES, ALL_APP_ROLES, APP_ROLES, type AppRole } from './appRoles';
import { ProgramChangesPage } from '../pages/ProgramChangesPage';
import { MonthlyReviewsPage } from '../pages/MonthlyReviewsPage';
import { FiscalEndReviewPage } from '../pages/FiscalEndReviewPage';
import { ChangeHistoryPage } from '../pages/ChangeHistoryPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { PdmAssignmentPage } from '../pages/PdmAssignmentPage';
import { AlertBannersPage } from '../pages/AlertBannersPage';
import { HelpPage } from '../pages/HelpPage';
import { DevsPage } from '../pages/DevsPage';

export interface AppRoute {
  /** Router path. Must be unique across the registry. */
  path: string;
  /** Label shown in the side navigation and in entitlement copy. */
  label: string;
  /** Human readable area this route belongs to, filled in from its group. */
  group: string;
  /** App roles allowed to open the route (OR semantics). */
  roles: AppRole[];
  icon: React.ReactElement;
  element: React.ReactElement;
  /** Hide from the side navigation while keeping the route reachable. */
  hideFromNav?: boolean;
  /**
   * Keep the route out of `defaultRoute` and `suggestedRoute`. Support pages are
   * reachable by every role, so without this they would win the landing redirect
   * for users whose own area is registered further down, and would be offered as
   * the "go here instead" CTA in place of a page that actually does their work.
   */
  excludeFromDefaults?: boolean;
}

export interface AppRouteGroup {
  label: string;
  routes: AppRoute[];
}

type AppRouteDefinition = Omit<AppRoute, 'group'>;

interface AppRouteGroupDefinition {
  label: string;
  routes: AppRouteDefinition[];
}

const routeGroupDefinitions: AppRouteGroupDefinition[] = [
  {
    label: 'Marketing operations',
    routes: [
      {
        path: '/monthlyreviews',
        label: 'Monthly reviews',
        roles: MARKETING_ROLES,
        icon: <PersonAvailableRegular />,
        element: <MonthlyReviewsPage />,
      },
      {
        path: '/fiscalendreview',
        label: 'Fiscal end review',
        roles: MARKETING_ROLES,
        icon: <CalendarLtrRegular />,
        element: <FiscalEndReviewPage />,
      },
      {
        path: '/changehistory',
        label: 'Change history',
        roles: MARKETING_ROLES,
        icon: <ClockRegular />,
        element: <ChangeHistoryPage />,
      },
      {
        path: '/programchanges',
        label: 'Program changes',
        roles: MARKETING_ROLES,
        icon: <EditRegular />,
        element: <ProgramChangesPage />,
      },
      // Notifications stays routable but hidden from the nav until it ships.
      {
        path: '/notifications',
        label: 'Notifications',
        roles: MARKETING_ROLES,
        icon: <AlertRegular />,
        element: <NotificationsPage />,
        hideFromNav: true,
      },
      {
        path: '/help',
        label: 'Help',
        roles: ALL_APP_ROLES,
        icon: <QuestionCircleRegular />,
        element: <HelpPage />,
        excludeFromDefaults: true,
      },
    ],
  },
  {
    label: 'Partner coverage',
    routes: [
      {
        path: '/pdm-assignment',
        label: 'PDM assignment',
        roles: PDM_READ_ROLES,
        icon: <PersonAddRegular />,
        element: <PdmAssignmentPage />,
      },
      {
        path: '/alert-banners',
        label: 'Alert banners',
        roles: MARKETING_ROLES,
        icon: <AlertRegular />,
        element: <AlertBannersPage />,
      },
    ],
  },
  {
    label: 'Admin',
    routes: [
      {
        path: '/programchanges/devs',
        label: 'Developer tools',
        roles: [APP_ROLES.administrator],
        icon: <WrenchRegular />,
        element: <DevsPage />,
      },
    ],
  },
];

export const APP_ROUTE_GROUPS: AppRouteGroup[] = routeGroupDefinitions.map((group) => ({
  label: group.label,
  routes: group.routes.map((route) => ({ ...route, group: group.label })),
}));

export const ALL_ROUTES: AppRoute[] = APP_ROUTE_GROUPS.flatMap((group) => group.routes);

export function findRouteByPath(path: string | undefined): AppRoute | undefined {
  if (!path) return undefined;
  return ALL_ROUTES.find((route) => route.path === path);
}
